import { useEffect, useState } from 'react';
import { useContractWrite, useNetwork, useSigner, useWaitForTransaction } from 'wagmi';

import { abi } from 'constants/contract';
import { CONTRACT_ADDRESS, MINTING_FEE_WEI } from 'constants/global';
import { getNftVoucher, TxnState, CHAIN } from 'services/nfts';
import * as Types from 'types';

import { useWagmi } from './useWagmi';

export const useContract = (certificateTransactionHash: string | undefined, editionId: number) => {
  const [voucher, setVoucher] = useState<{
    tokenId: string;
    signature: string;
    issuerAddress: string;
  }>();
  const [txnHash, setTxnHash] = useState(certificateTransactionHash);
  const [internalError, setInternalError] = useState<Types.RenderableError<any> | undefined>();

  const { data: signer } = useSigner();
  const { chain: activeChain } = useNetwork();

  const { verifiedAddress } = useWagmi();

  // if (voucher?.signature) {
  //   console.log({
  //     redeemer: verifiedAddress,
  //     minPrice: MINTING_FEE_WEI,
  //     tokenId: voucher?.tokenId,
  //     issuer: voucher?.issuerAddress,
  //     signature: voucher?.signature,
  //     contract: CONTRACT_ADDRESS,
  //   });
  // }

  const {
    data: txnRecipt,
    error: txnError,
    isLoading: txnLoading,
    isError,
    isSuccess, // the transaction was succeffull but it's still pending computation finish
    write,
  } = useContractWrite({
    addressOrName: CONTRACT_ADDRESS,
    contractInterface: abi,
    signerOrProvider: signer,
    functionName: 'redeem',
    chainId: CHAIN.id,
    args: {
      redeemer: verifiedAddress,
      minPrice: MINTING_FEE_WEI,
      tokenId: voucher?.tokenId,
      issuer: voucher?.issuerAddress,
      signature: voucher?.signature,
    },
    overrides: {
      maxFeePerGas: 80000000000,
      maxPriorityFeePerGas: 3000000000,
      gasLimit: 200000,
      value: MINTING_FEE_WEI,
    },
    onSuccess(data) {
      setTxnHash(data.hash);
    },
    onError(e) {
      bugsnag.notify(e);
    },
  });

  const { data: transaction } = useWaitForTransaction({
    hash: txnHash,
    wait: txnRecipt?.wait,
  });

  let txnState: TxnState = 'idle';

  switch (true) {
    case Boolean(certificateTransactionHash):
      // Transaction is already indexed.
      txnState = 'success';
      break;
    case transaction?.status === 1:
      // Transaction now lives on Blockchain.
      txnState = 'success';
      break;
    case transaction?.status === 0:
      // Transaction failed at contract level.
      txnState = 'reverted';
      break;
    case isSuccess:
      txnState = 'pending';
      break;
    case isError:
      // Ignore UserRejectedRequestError error
      if (txnError && txnError.name === 'UserRejectedRequestError') {
        txnState = 'idle';
      } else {
        // Transaction failed before reaching Blockchain.
        txnState = 'error';
      }
      break;
    case txnLoading:
      txnState = 'loading';
      break;
  }

  useEffect(() => {
    const fetchVoucher = async () => {
      setInternalError(undefined);
      if (activeChain === undefined || verifiedAddress === undefined) {
        return;
      }

      const { data, error } = await getNftVoucher(activeChain.id, editionId, verifiedAddress);
      if (error) {
        setInternalError(error);
      } else {
        setVoucher(data);
      }
    };

    if (!transaction) {
      fetchVoucher();
    }
  }, [activeChain?.id, verifiedAddress, transaction]);

  return {
    txnHash,
    txnRecipt,
    transaction,
    voucher,
    txnState,
    txnError,
    internalError,
    write,
    setVoucher,
  };
};
